import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import {
  GridOptionsExt,
  EventScheduleDialog,
  RRuleFns,
  UtilDialogService,
  AgFns,
  ProxAgFns,
  ISortModel,
  wrapBusy,
  StatusService,
} from '@core';
import {
  ActiveStatusEnum,
  Manifest,
  ProgramBillingConfig,
  ProgramTypeEnum,
  ProgramUserGroup,
  Supplier,
  SupplierSubmissionStatusEnum,
} from '@models';
import { DialogService } from 'primeng/dynamicdialog';
import { ProgramFrmComponent } from './program-frm.component';
import { AccountDbQueryService } from '../services/account-db-query.service';
import { AccountDbSaveService } from '../services/account-db-save.service';
import { ToastrService } from 'ngx-toastr';
import * as _ from 'lodash';
import { Router } from '@angular/router';
import { CellValueChangedEvent, ColDef, EditableCallbackParams, GridApi, GridReadyEvent, ValueSetterParams } from '@ag-grid-community/core';
import { UtilFns } from '@utils';
import { EntityFns } from '@data';
import { AccountAddressFinderDialog } from '../addresses/account-address-finder.dialog';

@Component({
  selector: 'prox-program-billing-sub',
  templateUrl: './program-billing.sub.component.html',
  standalone: false,
})
export class ProgramBillingSubComponent implements OnInit, OnChanges {
  @Input() parent!: ProgramFrmComponent;
  @Input() visible!: boolean;

  suppliers: Supplier[] = [];
  suppliersToSelect: Supplier[] = [];
  supplierChecklist: { supplier: Supplier; isChecked: boolean }[] = [];

  pbcGridOptions: GridOptionsExt = {};
  programBillingConfigs: ProgramBillingConfig[] = [];
  programUserGroups: ProgramUserGroup[] = [];

  manifestGridOptions: GridOptionsExt = {};
  manifests: Manifest[] = [];
  manifestsToSelect: Manifest[] = [];

  showAddManifest = true;

  constructor(
    public dbQueryService: AccountDbQueryService,
    public dbSaveService: AccountDbSaveService,
    public dialogService: UtilDialogService,
    public pngDialogService: DialogService,
    public toastr: ToastrService,
    private router: Router,
    private statusService: StatusService,
  ) {
    //
  }

  get program() {
    return this.parent.program;
  }

  async ngOnInit() {
    // this.suppliers = _.uniq(this.program.programProductTypeConfigs.map((x) => x.productTypeConfig.pricedProductType.productType.supplier));
    this.suppliers = _.uniq(this.program.programSuppliers.map((ps) => ps.supplier));
    this.programBillingConfigs = this.program.programBillingConfigs;
    this.programUserGroups = this.program.approvalTree?.approvalTreeUserGroups.map((x) => x.programUserGroup) ?? [];

    this.programUserGroups.unshift({
      id: null as any,
      name: '- Default -',
    } as ProgramUserGroup);

    this.suppliersToSelect = this.suppliers.slice();
    this.suppliersToSelect.unshift({
      id: null as any,
      name: '- Any -',
    } as Supplier);

    this.manifests = this.program.account.manifests.slice();
    this.updateManifestsToSelect();

    this.pbcGridOptions = AgFns.initGridOptions(this, {
      onGridReady: this.onPbcGridReady,
      onCellValueChanged: this.onPbcCellValueChanged,
      // onRowSelected: this.onPbcRowSelected,

      rowModelType: 'clientSide',
      getRowId: (params) => params.data.id,
    });

    this.manifestGridOptions = AgFns.initGridOptions(this, {
      onGridReady: this.onManifestGridReady,
      // onRowSelected: this.onManifestRowSelected,

      rowModelType: 'clientSide',
      getRowId: (params) => params.data.id,
    });

    this.refresh();
  }

  @wrapBusy
  ngOnChanges(changes: SimpleChanges): void {
    this.refresh();
  }

  @wrapBusy
  async refresh(forceRefresh = false) {
    if (!this.visible && !forceRefresh) return;
    this.parent.updateComponentNameMap(this);

    // needed to refresh product type tag dropdowns and editability
    this.onPbcGridReady(this.pbcGridOptions);
    this.onManifestGridReady(this.manifestGridOptions);

    AgFns.refreshGrid(this.pbcGridOptions, this.programBillingConfigs);
    AgFns.refreshGrid(this.manifestGridOptions, this.manifests);
  }

  updateColDef(gridApi: GridApi, newColDef: ColDef) {
    const curColDefs = gridApi.getColumnDefs();
    const updatedColumnDefs = curColDefs?.map((colDef) => {
      if (AgFns.isColGroupDef(colDef)) {
        // doesn't handle nested colDefs yet.
        return colDef;
      }
      if (colDef.colId === newColDef.colId) {
        return newColDef;
      } else {
        return colDef;
      }
    });
    gridApi.setGridOption('columnDefs', updatedColumnDefs);
  }

  onPbcGridReady(event: GridReadyEvent | GridOptionsExt) {
    const colDefs = [
      ProxAgFns.getEntityStateColDef(),
      {
        headerName: 'Program User Group Name',
        ...AgFns.createDropdownEditor('programUserGroupId', this.programUserGroups),
        editable: !this.parent.isReadOnly(),
        ...AgFns.flexWidth(100),
        colId: 'programUserGroup.name',
      },
      {
        headerName: 'Billing Address',
        valueGetter: (params) => params.data?.billingAccountAddress?.description ?? '[Select]',
        ...AgFns.createCellClickedProps(this.onSelectPbcBillingAddress.bind(this)),
        editable: false,
        ...AgFns.flexWidth(150),
        colId: 'billingAddressId',
      },
      {
        headerName: 'Invoice Email',
        editable: !this.parent.isReadOnly(),
        field: 'invoiceEmail',
        valueSetter: (params: ValueSetterParams) => {
          const pbc = params.data as ProgramBillingConfig;
          pbc.invoiceEmail = params.newValue;
          // pbc.portalName = undefined;
          pbc.manifestId = undefined;
          return true;
        },
        ...AgFns.flexWidth(150),
      },
      {
        headerName: 'Portal Name',
        editable: !this.parent.isReadOnly(),
        field: 'portalName',
        valueSetter: (params: ValueSetterParams) => {
          const pbc = params.data as ProgramBillingConfig;
          pbc.portalName = params.newValue;
          // pbc.invoiceEmail = undefined;
          pbc.manifestId = undefined;
          return true;
        },
        ...AgFns.flexWidth(150),
      },
      {
        headerName: 'Manifest',
        ...AgFns.createDropdownEditor('manifestId', this.manifestsToSelect),
        editable: !this.parent.isReadOnly(),
        // can't use a valueSetter here because the createDropdownEditor is already creating one.
        ...AgFns.flexWidth(100),
        colId: 'manifestId',
      },

      {
        headerName: 'Supplier',
        ...AgFns.createDropdownEditor('supplierId', this.suppliersToSelect),
        editable: this.isSupplierEditable.bind(this),
        ...AgFns.flexWidth(120),
        colId: 'supplierId',
      },

      this.parent.ifEditable(ProxAgFns.getEntityDeleteColDef(this.onDeletePbc.bind(this))),
    ];
    const sortModel: ISortModel = [{ colId: 'programUserGroup.name', sort: 'asc' }];
    AgFns.initGrid(event, colDefs, sortModel);
  }

  onPbcCellValueChanged(event: CellValueChangedEvent) {
    const pbc = event.data as ProgramBillingConfig;
    const colId = event.colDef.colId ?? event.colDef.field;
    if (colId == 'manifestId') {
      pbc.billingAccountAddressId = undefined;
      pbc.invoiceEmail = undefined;
      pbc.portalName = undefined;
      pbc.supplierId = pbc.manifest?.supplierId;
      AgFns.refreshGrid(this.pbcGridOptions, this.programBillingConfigs);
    } else if (colId == 'supplierId') {
      // No cascading — supplierId on a billing-address row simply scopes it to one supplier.
      AgFns.refreshGrid(this.pbcGridOptions, this.programBillingConfigs);
    }
  }

  /** Supplier is editable only when the row has no Manifest (manifest forces the supplier). */
  isSupplierEditable(params: EditableCallbackParams | ProgramBillingConfig): boolean {
    if (this.parent.isReadOnly()) return false;
    const pbc = params instanceof ProgramBillingConfig ? params : (params.data as ProgramBillingConfig);
    return pbc?.manifestId == null;
  }

  onManifestGridReady(event: GridReadyEvent | GridOptionsExt) {
    const colDefs = [
      ProxAgFns.getEntityStateColDef(),
      {
        headerName: 'Name',
        field: 'name',
        filter: 'agTextColumnFilter',
        editable: this.isManifestEditable.bind(this),
        ...AgFns.flexWidth(140),
      },
      {
        headerName: 'Supplier',
        ...AgFns.createDropdownEditor('supplierId', this.suppliers),
        editable: this.isManifestEditable.bind(this),
        ...AgFns.flexWidth(140),
      },
      {
        headerName: 'Billing Address',
        valueGetter: (params) => params.data?.billingAccountAddress?.description ?? '[Select]',
        filter: 'agTextColumnFilter',
        ...AgFns.createCellClickedProps(this.onSelectManifestBillingAddress.bind(this)),
        editable: this.isManifestEditable.bind(this),
        ...AgFns.flexWidth(140),
      },
      {
        headerName: 'Invoice Email',
        editable: !this.parent.isReadOnly(),
        field: 'invoiceEmail',
        ...AgFns.flexWidth(140),
      },
      {
        headerName: 'Portal Name',
        editable: !this.parent.isReadOnly(),
        field: 'portalName',
        ...AgFns.flexWidth(140),
      },
      {
        headerName: 'Schedule',
        ...AgFns.createCellClickedProps(this.editManifestSchedule.bind(this)),
        valueGetter: (params) => {
          const manifest = params.data as Manifest;
          return this.getManifestScheduleText(manifest) ?? '[Select]';
        },
        ...AgFns.flexWidth(140),
      },
      {
        headerName: 'Include Invoices?',
        field: 'shouldIncludeInvoices',
        editable: this.isManifestEditable.bind(this),
        ...AgFns.fixWidth(140),
      },

      ProxAgFns.getEntityStatusColDef(this.onChangeActiveStatus.bind(this), { canDisplay: this.canChangeStatus.bind(this) }),
      ProxAgFns.getEntityDeleteColDef(this.onDeleteManifest.bind(this)),
    ];
    const sortModel: ISortModel = [{ colId: 'name', sort: 'asc' }];

    AgFns.initGrid(event, colDefs, sortModel);
    AgFns.selectFirstRow(this.manifestGridOptions);
  }

  canChangeStatus(manifest: Manifest) {
    return !this.parent.isReadOnly();
  }

  isManifestEditable(params: EditableCallbackParams | Manifest): boolean {
    const manifest = params instanceof Manifest ? params : (params.data as Manifest);
    if (this.parent.isReadOnly()) return false;
    if (manifest.supplierSubmissionStatusId == SupplierSubmissionStatusEnum.Sent) {
      return false;
    }
    return true;
  }

  @wrapBusy
  async onAddPbc() {
    if (this.programBillingConfigs == null) return;

    const pbc = this.dbSaveService.createEntity(ProgramBillingConfig, {
      programId: this.program.id,
    });
    pbc.entityAspect.validateEntity();

    this.programBillingConfigs?.push(pbc);
    AgFns.refreshGrid(this.pbcGridOptions, this.programBillingConfigs);
    await UtilFns.wait(0);
    AgFns.selectGridRowByKey(this.pbcGridOptions, (e) => e.id, pbc.id);
  }

  async onDeletePbc(pbc: ProgramBillingConfig) {
    if (pbc.programUserGroupId == null && this.programBillingConfigs.filter((x) => x.programUserGroupId == null).length == 1) {
      await this.dialogService.okDialog('Cannot delete', 'You cannot delete the only [Default] Program Billing Config.');
      return;
    }

    const ok = await this.dialogService.askIfOk(
      'Delete Program Billing Config',
      'Are you sure you want to delete this Program Billing Config?',
    );
    if (!ok) return;

    // ok to delete a pbc because job orders only reference the programUserGroupId directly.
    // deleting my invalidate a job order in flight but not one that is already posted.
    EntityFns.deleteOrDetach(pbc.entityAspect);
    this.programBillingConfigs = this.program.programBillingConfigs.slice();
    AgFns.refreshGrid(this.pbcGridOptions, this.programBillingConfigs);
  }

  async onSelectPbcBillingAddress(pbc: ProgramBillingConfig) {
    const addr = await this.onSelectBillingAddress();
    if (addr != null) {
      pbc.billingAccountAddressId = addr.id;
      pbc.manifestId = undefined;
      // Note: pbc.supplierId is preserved here. A billing-address row may carry a supplier (shape B).
      // Only the Manifest cascade clears supplier (manifest forces its own supplier).
      AgFns.refreshGrid(this.pbcGridOptions, this.programBillingConfigs);
    }
  }

  async onSelectManifestBillingAddress(manifest: Manifest) {
    if (!this.isManifestEditable(manifest)) return;
    const addr = await this.onSelectBillingAddress();
    if (addr != null) {
      manifest.billingAccountAddressId = addr.id;

      AgFns.refreshGrid(this.manifestGridOptions, this.manifests);
    }
  }

  async onSelectBillingAddress() {
    if (this.parent.isReadOnly()) return null;
    const addrs = await this.dialogService.createFinder(AccountAddressFinderDialog, {
      accountId: this.program.accountId,
      addressType: 'B',
    });
    return addrs.length > 0 ? addrs[0] : null;
  }

  canEditBillingAddress() {
    return !this.parent.hasChanges();
    // return this.canChangeAddress && !this.parent.hasChanges();
  }

  onAddManifest() {
    if (this.program.programTypeId === ProgramTypeEnum.Inventory) {
      this.dialogService.okDialog('Unable to add manifest', 'Manifests are not allowed for Inventory Programs');
      return;
    }

    if (this.suppliers.length == this.programBillingConfigs.filter((x) => x.manifest != null).length) {
      this.dialogService.okDialog(
        'Unable to add manifest',
        'You cannot have more manifests than Proximity Suppliers for a single Proximity Program',
      );
      return;
    }
    const manifest = this.dbSaveService.createEntity(Manifest, {
      name: 'Manifest for: ' + this.program.name,
      accountId: this.program.accountId,
      supplierId: this.suppliers[0].id,
      supplierSubmissionStatusId: SupplierSubmissionStatusEnum.New,
      activeStatusId: ActiveStatusEnum.Active,
    });
    this.manifests = this.program.account.manifests.slice();
    AgFns.refreshGrid(this.manifestGridOptions, this.manifests);

    this.updateManifestsToSelect();

    AgFns.refreshGrid(this.pbcGridOptions, this.programBillingConfigs);
  }

  async onDeleteManifest(manifest: Manifest) {
    if (manifest.supplierSubmissionStatusId == SupplierSubmissionStatusEnum.Sent) {
      this.dialogService.okDialog('Cannot delete', 'This manifest has already been sent to the supplier.');
      return;
    }

    let inUse = await this.dbQueryService.checkIfInUse(manifest, ProgramBillingConfig, 'manifestId');
    if (!inUse) {
      inUse = this.programBillingConfigs.some((pbc) => pbc.manifestId === manifest.id);
    }
    if (inUse) {
      this.dialogService.okDialog('Cannot delete', 'This manifest is in use and cannot be deleted.');
      return;
    }

    EntityFns.deleteOrDetach(manifest.entityAspect);
    this.manifests = this.program.account.manifests.slice();

    AgFns.refreshGrid(this.manifestGridOptions, this.manifests);
    this.updateManifestsToSelect();

    AgFns.refreshGrid(this.pbcGridOptions, this.programBillingConfigs);
  }

  updateManifestsToSelect() {
    this.manifestsToSelect = this.manifests.slice();
    this.manifestsToSelect.unshift({
      id: undefined as any,
      name: '- [None] -',
    } as Manifest);

    if (this.pbcGridOptions.apiX == null) return;
    this.updateColDef(this.pbcGridOptions.apiX!, {
      headerName: 'Manifest',
      ...AgFns.createDropdownEditor('manifestId', this.manifestsToSelect),
      editable: true,
      ...AgFns.flexWidth(100),
      colId: 'manifestId',
    });
  }

  async editManifestSchedule(manifest: Manifest) {
    if (!this.isManifestEditable(manifest)) return;
    const r = await EventScheduleDialog.openDialog(this.pngDialogService, {
      rruleString: manifest.rRule,
    });
    if (r != null) {
      manifest.rRule = r;
    }
    AgFns.refreshGrid(this.manifestGridOptions, this.manifests);
  }

  async onChangeActiveStatus(row: Manifest) {
    // TODO: PhaseOut logic not yet implemented — hide PhaseOut from status picker until it is.
    const isAvailableFn = (s) => {
      return s.id !== ActiveStatusEnum.Phaseout;
    };

    await this.statusService.onChangeActiveStatus(this, row, 'Manifest', undefined, isAvailableFn);
    this.manifestGridOptions.apiX?.refreshCells();
  }

  getManifestScheduleText(manifest: Manifest) {
    if (manifest?.rRule == null || manifest.rRule.trim() === '') {
      return null;
    }
    const rrule = RRuleFns.fromString(manifest.rRule);
    return RRuleFns.toText(rrule);
  }

  gotoBillingAddress(pbc: ProgramBillingConfig) {
    if (pbc.billingAccountAddressId == null) return;
    this.router.navigate([
      '/account/accounts',
      this.parent.accountId,
      'manage',
      'billing-account-addresses',
      pbc.billingAccountAddressId,
      'B',
    ]);
  }
}
